import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { Badge, Button, Modal } from '@lipaflow/ui'
import {
  adminService,
  type SupportSessionResponse,
  type Tenant,
  type TenantDetail,
  type TenantUpdatePayload,
  type TenantUserSummary,
} from '../../services/admin'
import { OperatingModelControl } from '../../components/OperatingModelControl'
import { ProductControl } from '../../components/ProductControl'
import { SubscriptionSection } from './SubscriptionSection'
import { AdjustmentsSection } from './AdjustmentsSection'
import { OffboardSection } from './OffboardSection'
import { ActivationSection } from './ActivationSection'
import { AdminInvitationSection } from './AdminInvitationSection'
import { apiErrorMessage } from '@lipaflow/shared'

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const labelCls = 'block text-caption text-ink-muted mb-1'

type Mode = 'view' | 'edit' | 'suspend' | 'activate' | 'signInAs' | 'support'

/** The connected collection account, as payers are told it (payment integrity P4). */
function collectionAccount(d: TenantDetail): string {
  if (d.paybill_number) return `M-PESA paybill ${d.paybill_number}`
  if (d.bank_account) return [d.bank_name, d.bank_account].filter(Boolean).join(' · ')
  return 'Not connected'
}

type EditForm = TenantUpdatePayload & { name: string; short_name: string }

/**
 * Only what the admin changed: a save must not resend (and so revalidate)
 * fields nobody touched. A box emptied by the admin means "clear it".
 */
function changedFields(original: EditForm, form: EditForm): TenantUpdatePayload {
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(form) as (keyof EditForm)[]) {
    const before = typeof original[key] === 'string' ? (original[key] as string).trim() : original[key]
    const after = typeof form[key] === 'string' ? (form[key] as string).trim() : form[key]
    if (before === after) continue
    out[key] = after === '' ? null : after
  }
  return out as TenantUpdatePayload
}

/**
 * One customer and everything LipaFlow staff can do for them, in one panel:
 * details, how they are run, edit, suspend or reactivate, and the two support
 * entry points. The support flows keep every safeguard they had as separate
 * dialogs: a named verified user, admins only for support access, and typing
 * the customer's short name to confirm.
 */
export function CustomerPanel({
  customer,
  productLabel,
  onClose,
}: {
  customer: Tenant | null
  productLabel?: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>('view')
  const [form, setForm] = useState<EditForm>({ name: '', short_name: '' })
  const [original, setOriginal] = useState<EditForm>({ name: '', short_name: '' })
  const [targetUserId, setTargetUserId] = useState('')
  const [confirmText, setConfirmText] = useState('')

  const detail = useQuery({
    queryKey: ['tenants', customer?.id, 'detail'],
    queryFn: () => adminService.getTenant(customer!.id),
    enabled: !!customer,
  })
  const users = useQuery({
    queryKey: ['tenant-users', customer?.id],
    queryFn: () => adminService.listTenantUsers(customer!.id),
    enabled: !!customer && (mode === 'signInAs' || mode === 'support'),
  })

  const reset = () => { setMode('view'); setTargetUserId(''); setConfirmText('') }
  const close = () => { reset(); onClose() }
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['tenants'] })
  }

  const update = useMutation({
    mutationFn: (data: TenantUpdatePayload) => adminService.updateTenant(customer!.id, data),
    onSuccess: () => { toast.success('Customer details saved'); refresh(); reset() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't save the changes")),
  })
  const suspend = useMutation({
    mutationFn: () => adminService.suspendTenant(customer!.id),
    onSuccess: () => { toast.success('Customer suspended'); refresh(); close() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't suspend the customer")),
  })
  const activate = useMutation({
    mutationFn: () => adminService.activateTenant(customer!.id),
    onSuccess: () => { toast.success('Customer reactivated'); refresh(); close() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't reactivate the customer")),
  })
  const openSession = (data: SupportSessionResponse) => {
    // With "noopener" window.open always returns null, so the old blocked-popup
    // fallback ran every time and took the console away too. Open, then cut
    // the new tab's link back to the console; only a real block falls back.
    const popup = window.open(data.launch_url, '_blank')
    if (!popup) {
      window.location.assign(data.launch_url)
      return
    }
    popup.opener = null
    toast.success(`Opened ${data.target_user.full_name} in a new tab`)
    close()
  }
  const signInAs = useMutation({
    mutationFn: () => adminService.startUserSudo(customer!.id, targetUserId),
    onSuccess: openSession,
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't sign in as that user")),
  })
  const support = useMutation({
    mutationFn: () => adminService.startSupportAccess(customer!.id, targetUserId),
    onSuccess: openSession,
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't open support access")),
  })

  if (!customer) return <Modal open={false} onClose={close}>{null}</Modal>

  const d: TenantDetail | undefined = detail.data
  const confirmed = confirmText.trim().toLowerCase() === customer.short_name.trim().toLowerCase()
  const eligible: TenantUserSummary[] = (users.data ?? []).filter(
    (u) => u.is_verified && (mode !== 'support' || u.is_administrator),
  )
  const busy = update.isPending || suspend.isPending || activate.isPending || signInAs.isPending || support.isPending

  const changes = changedFields(original, form)
  const startEdit = () => {
    if (!d) return
    const loaded: EditForm = {
      name: d.name,
      short_name: d.short_name,
      description: d.description || '',
      email: d.email || '',
      phone: d.phone || '',
      address: d.address || '',
      logo_url: d.logo_url || '',
      brand_color: d.brand_color || '#006D77',
      ai_enabled: d.ai_enabled,
      outbound_enabled: d.outbound_enabled,
    }
    setOriginal(loaded)
    setForm(loaded)
    setMode('edit')
  }

  return (
    <Modal open onClose={close} title={customer.name} size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={customer.is_active ? 'success' : 'neutral'}>{customer.is_active ? 'Active' : 'Not active'}</Badge>
          {productLabel && <Badge variant="teal">{productLabel}</Badge>}
          <span className="text-body-sm text-ink-muted font-mono">{customer.short_name}</span>
        </div>

        {detail.isLoading ? (
          <p role="status" className="text-body-sm text-ink-muted">Loading customer…</p>
        ) : d ? (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Fact label="Contact email" value={d.email || '—'} />
              <Fact label="Phone" value={d.phone || '—'} />
              <Fact
                label="Payers pay into"
                value={collectionAccount(d)}
                sub={d.paybill_number || d.bank_account ? 'From their connected account' : 'Connect one under Payment connections'}
              />
              <Fact label="Messages to payers" value={d.outbound_enabled ? 'On' : 'Paused'} />
              <Fact label="Amani replies" value={d.ai_enabled ? 'On' : 'Off'} />
            </dl>

            <ActivationSection tenantId={d.id} />

            <AdminInvitationSection tenantId={d.id} />

            <SubscriptionSection tenantId={d.id} />

            <AdjustmentsSection tenantId={d.id} tenantName={d.name} />

            <OperatingModelControl key={`${d.id}:${d.operating_model ?? ''}`} tenant={d} onChanged={() => refresh()} />
            <ProductControl key={`${d.id}:product`} tenant={d} onChanged={() => refresh()} />

            <OffboardSection tenantId={d.id} tenantName={d.name} />

            <details className="rounded-card border border-hairline">
              <summary className="cursor-pointer px-4 py-3 text-body-sm font-medium text-ink">Technical details</summary>
              <div className="px-4 pb-4 space-y-3">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Fact label="Customer ID" value={<span className="font-mono text-body-sm break-all">{d.id}</span>} />
                  <Fact label="WhatsApp number ID" value={<span className="font-mono text-body-sm break-all">{d.whatsapp_phone_number_id || '—'}</span>} />
                  <Fact
                    label="Amani usage this month"
                    value={<span className="tabular">{d.ai_tokens_used_this_month.toLocaleString()} of {d.ai_token_budget_monthly.toLocaleString()} tokens</span>}
                  />
                </dl>
                <div>
                  <p className={labelCls}>Raw settings (JSON)</p>
                  <pre className="bg-surface-alt rounded-button border border-hairline p-3 text-caption text-ink overflow-x-auto">
                    {JSON.stringify(d.settings || {}, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className={labelCls}>Product config overrides (JSON)</p>
                  <pre className="bg-surface-alt rounded-button border border-hairline p-3 text-caption text-ink overflow-x-auto">
                    {JSON.stringify(d.vertical_config || {}, null, 2)}
                  </pre>
                </div>
              </div>
            </details>
          </>
        ) : (
          <p className="text-body-sm text-warning">Couldn't load this customer's details.</p>
        )}

        {mode === 'view' && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
            <Button variant="secondary" onClick={startEdit} disabled={!d}>Edit details</Button>
            <Button variant="secondary" onClick={() => { reset(); setMode('support') }}>Open support access</Button>
            <Button variant="ghost" onClick={() => { reset(); setMode('signInAs') }}>Sign in as one of their users</Button>
            {customer.is_active ? (
              <Button variant="ghost" onClick={() => setMode('suspend')}>Suspend</Button>
            ) : (
              <Button variant="ghost" onClick={() => setMode('activate')}>Reactivate</Button>
            )}
          </div>
        )}

        {mode === 'edit' && (
          <div className="pt-4 border-t border-hairline space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label htmlFor="c-name" className={labelCls}>Name</label>
                <input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
              </div>
              <div>
                <label htmlFor="c-short" className={labelCls}>Short name</label>
                <input id="c-short" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value.toLowerCase() })} className={clsx(field, 'font-mono')} />
              </div>
              <div>
                <label htmlFor="c-email" className={labelCls}>Contact email</label>
                <input id="c-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
              </div>
              <div>
                <label htmlFor="c-phone" className={labelCls}>Phone</label>
                <input id="c-phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
              </div>
              <div>
                <label htmlFor="c-address" className={labelCls}>Address</label>
                <input id="c-address" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} className={field} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="c-description" className={labelCls}>Description</label>
                <textarea id="c-description" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} />
              </div>
              <div>
                <label htmlFor="c-logo" className={labelCls}>Logo URL</label>
                <input id="c-logo" type="url" value={form.logo_url || ''} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className={field} />
              </div>
              <div>
                <label htmlFor="c-color" className={labelCls}>Brand colour</label>
                <div className="flex items-center gap-2">
                  <input type="color" aria-label="Pick brand colour" value={form.brand_color || '#006D77'} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 w-12 rounded-button border border-hairline" />
                  <input id="c-color" value={form.brand_color || ''} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className={clsx(field, 'font-mono')} />
                </div>
              </div>
            </div>
            <Toggle
              label="Messages to payers"
              hint="Reminders, receipts and replies from this customer"
              on={!!form.outbound_enabled}
              onChange={(v) => setForm({ ...form, outbound_enabled: v })}
            />
            <Toggle
              label="Amani replies"
              hint="When off, payers get the customer's standard templates"
              on={!!form.ai_enabled}
              onChange={(v) => setForm({ ...form, ai_enabled: v })}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={reset}>Back</Button>
              <Button
                disabled={!form.name.trim() || !form.short_name.trim() || Object.keys(changes).length === 0 || busy}
                isLoading={update.isPending}
                onClick={() => update.mutate(changes)}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {(mode === 'suspend' || mode === 'activate') && (
          <div className="pt-4 border-t border-hairline space-y-2">
            <p className="text-body-sm text-ink">
              {mode === 'suspend'
                ? `Suspending ${customer.name} switches off their account and pauses their subscription. Their staff can't sign in until you reactivate them.`
                : `Reactivate ${customer.name}? Their staff will be able to sign in again.`}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={reset}>Back</Button>
              <Button
                variant="secondary"
                isLoading={suspend.isPending || activate.isPending}
                onClick={() => (mode === 'suspend' ? suspend.mutate() : activate.mutate())}
              >
                {mode === 'suspend' ? 'Suspend customer' : 'Reactivate customer'}
              </Button>
            </div>
          </div>
        )}

        {(mode === 'signInAs' || mode === 'support') && (
          <div className="pt-4 border-t border-hairline space-y-3">
            <p className="text-body-sm text-ink">
              {mode === 'signInAs'
                ? `Opens ${customer.name}'s app in a new tab as the user you choose, with exactly their role — for troubleshooting what they see.`
                : `Opens ${customer.name}'s app in a new tab as one of their administrators, for support fixes and configuration.`}
            </p>
            <div>
              <label htmlFor="target-user" className={labelCls}>{mode === 'support' ? 'Administrator' : 'User'}</label>
              {users.isLoading ? (
                <p role="status" className="text-body-sm text-ink-muted">Loading their users…</p>
              ) : users.isError ? (
                <p role="alert" className="text-body-sm text-warning">
                  {apiErrorMessage(users.error, "Couldn't load their users. Try again.")}
                </p>
              ) : eligible.length === 0 ? (
                <p className="text-body-sm text-warning">
                  {mode === 'support' ? 'No verified administrator yet.' : 'No verified users yet.'}
                </p>
              ) : (
                <select id="target-user" value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} className={field}>
                  <option value="">Choose…</option>
                  {eligible.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name}{u.role ? ` (${u.role.replace(/_/g, ' ')})` : ''} · {u.email}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label htmlFor="confirm-short" className={labelCls}>
                Type <span className="font-mono text-ink">{customer.short_name}</span> to confirm
              </label>
              <input id="confirm-short" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className={clsx(field, 'font-mono')} autoComplete="off" />
            </div>
            <p className="text-caption text-ink-muted">This is recorded in the activity log.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={reset}>Back</Button>
              <Button
                disabled={!targetUserId || !confirmed || busy}
                isLoading={signInAs.isPending || support.isPending}
                onClick={() => (mode === 'signInAs' ? signInAs.mutate() : support.mutate())}
              >
                {mode === 'signInAs' ? 'Sign in as this user' : 'Open support access'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function Toggle({ label, hint, on, onChange }: { label: string; hint: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>
        <span className="block text-body-sm font-medium text-ink">{label}</span>
        <span className="block text-caption text-ink-muted">{hint}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => onChange(!on)}
        className={clsx('relative inline-flex h-6 w-11 items-center rounded-full transition-colors', on ? 'bg-accent' : 'bg-hairline')}
      >
        <span className={clsx('inline-block h-4 w-4 rounded-full bg-surface transition-transform', on ? 'translate-x-6' : 'translate-x-1')} />
      </button>
    </div>
  )
}

function Fact({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div>
      <dt className="text-caption text-ink-faint">{label}</dt>
      <dd className="text-body-sm text-ink mt-0.5">{value}</dd>
      {sub && <dd className="text-caption text-ink-muted">{sub}</dd>}
    </div>
  )
}
